import html2pdf from "html2pdf.js";

// The eKAS template is 1470x2214, so at the 762px height used by .ekas-img it renders
// 506px wide. Capturing at exactly that size keeps the PDF 1:1 with the overlay
// positions calibrated in App.css, so the form has only one set of coordinates.
const FORM_WIDTH_PX = 506;
const FORM_HEIGHT_PX = 762;

// Free-text boxes that can outgrow their cell on the form. Sizes match the cells
// measured on the form image (width x usable height, in the same 762px scale).
const SHRINK_TARGETS = [
  { selector: ".ekas-patient-hci-name", width: 186, height: 19, maxFont: 9, minFont: 5 },
  { selector: ".ekas-patient-name", width: 219, height: 17, maxFont: 8, minFont: 5 },
];

// App.css sets font-size with !important, so the inline override needs it too.
const setFontSize = (el, sizePx) =>
  el.style.setProperty("font-size", `${sizePx}px`, "important");

const shrinkToFit = (el, maxWidthPx, maxHeightPx, maxFontPx, minFontPx) => {
  if (!el) return;
  el.style.width = `${maxWidthPx}px`;
  el.style.maxWidth = `${maxWidthPx}px`;
  el.style.maxHeight = `${maxHeightPx}px`;
  el.style.overflow = "hidden";

  let fontSize = maxFontPx;
  setFontSize(el, fontSize);
  while (el.scrollHeight > maxHeightPx && fontSize > minFontPx) {
    fontSize -= 0.25;
    setFontSize(el, fontSize);
  }
};

const applyOverflowGuards = (root) => {
  SHRINK_TARGETS.forEach(({ selector, width, height, maxFont, minFont }) => {
    root
      .querySelectorAll(selector)
      .forEach((el) => shrinkToFit(el, width, height, maxFont, minFont));
  });
};

const applyEkasFormDimensions = (formRoot) => {
  const birFormEl =
    formRoot?.classList?.contains("bir-form") ? formRoot : formRoot?.querySelector?.(".bir-form");
  if (!birFormEl) return;

  birFormEl.style.position = "relative";
  birFormEl.style.width = `${FORM_WIDTH_PX}px`;
  birFormEl.style.height = `${FORM_HEIGHT_PX}px`;
  birFormEl.style.margin = "0";
  birFormEl.style.overflow = "hidden";

  const ekasImg = birFormEl.querySelector(".ekas-img");
  if (ekasImg) {
    ekasImg.style.position = "absolute";
    ekasImg.style.top = "0";
    ekasImg.style.left = "0";
    ekasImg.style.height = `${FORM_HEIGHT_PX}px`;
    ekasImg.style.width = "auto";
    ekasImg.style.margin = "0";
    ekasImg.style.objectFit = "unset";
  }
};

const waitForImage = (img) =>
  new Promise((resolve) => {
    if (!img) {
      resolve();
      return;
    }
    if (img.complete && img.naturalHeight !== 0) {
      resolve();
      return;
    }
    img.onload = resolve;
    img.onerror = resolve;
    setTimeout(resolve, 3000);
  });

/** Opens the browser print UI for eKAS HTML; resolves when the user finishes (Print or Cancel). */
const printEkasHtml = (formRoot) =>
  new Promise((resolve) => {
    const mount = document.createElement("div");
    mount.id = "ekas-print-mount";
    mount.className = "ekas-print-mount";
    const clone = formRoot.cloneNode(true);
    applyEkasFormDimensions(clone);
    applyOverflowGuards(clone);
    mount.appendChild(clone);
    document.body.appendChild(mount);
    document.body.classList.add("ekas-print-active");

    const pageStyle = document.createElement("style");
    pageStyle.id = "ekas-print-page-style";
    pageStyle.textContent = "@media print { @page { size: A4 portrait; margin: 0; } }";
    document.head.appendChild(pageStyle);

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.removeEventListener("afterprint", finish);
      document.body.classList.remove("ekas-print-active");
      pageStyle.remove();
      if (mount.parentNode) {
        mount.remove();
      }
      resolve();
    };

    window.addEventListener("afterprint", finish);

    const printMedia = window.matchMedia?.("print");
    if (printMedia) {
      const onPrintMediaChange = (event) => {
        if (!event.matches) finish();
      };
      printMedia.addEventListener("change", onPrintMediaChange, { once: true });
    }

    requestAnimationFrame(() => {
      window.print();
    });
  });

const waitForSourceReady = (printableElement, timeout = 5000) =>
  new Promise((resolve, reject) => {
    if (!printableElement) {
      reject(new Error("eKas content not ready."));
      return;
    }
    const start = Date.now();
    const check = () => {
      const hasContent = printableElement.innerHTML.trim().length >= 100;
      const img = printableElement.querySelector("img");
      if (hasContent && img) {
        resolve();
        return;
      }
      if (Date.now() - start > timeout) {
        if (hasContent) resolve();
        else reject(new Error("eKas content not ready."));
        return;
      }
      setTimeout(check, 100);
    };
    check();
  });

export async function generateEkasPdf(printableElement) {
  await waitForSourceReady(printableElement);

  const tempDiv = document.createElement("div");
  tempDiv.style.position = "fixed";
  tempDiv.style.top = "0";
  tempDiv.style.left = "-10000px";
  tempDiv.style.zIndex = "-1";
  tempDiv.style.width = `${FORM_WIDTH_PX}px`;
  tempDiv.style.height = `${FORM_HEIGHT_PX}px`;
  tempDiv.style.overflow = "hidden";
  tempDiv.style.backgroundColor = "white";
  tempDiv.style.pointerEvents = "none";
  tempDiv.setAttribute("aria-hidden", "true");
  tempDiv.innerHTML = printableElement.innerHTML;
  document.body.appendChild(tempDiv);

  try {
    const birFormEl = tempDiv.querySelector(".bir-form");
    if (!birFormEl) {
      throw new Error("eKas form not found.");
    }

    applyEkasFormDimensions(birFormEl);
    applyOverflowGuards(tempDiv);

    await waitForImage(tempDiv.querySelector("img"));
    await new Promise((resolve) => setTimeout(resolve, 500));

    const pdfBlob = await html2pdf()
      .set({
        margin: 0,
        filename: "ekas.pdf",
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 3,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          windowWidth: FORM_WIDTH_PX,
          windowHeight: FORM_HEIGHT_PX,
          width: FORM_WIDTH_PX,
          height: FORM_HEIGHT_PX,
        },
        jsPDF: {
          unit: "px",
          format: [FORM_WIDTH_PX, FORM_HEIGHT_PX],
          orientation: "portrait",
        },
      })
      .from(birFormEl)
      .outputPdf("blob");

    return pdfBlob;
  } finally {
    if (tempDiv.parentNode) {
      document.body.removeChild(tempDiv);
    }
  }
}

export async function generateAndPrintEkasPdf(printableElement) {
  const pdfBlob = await generateEkasPdf(printableElement);
  await printEkasHtml(printableElement);
  return pdfBlob;
}